import type {
  IDateIntervalInput,
  IDeliveryHierarchyParentInput,
  IEquipmentPosition,
  ILocationBase,
  ILocationWorkflowRestrictions,
  IWorkflowLocationRestriction,
} from '~/client/graph/types.generated'
import { RestrictionType } from '~/client/graph/types.generated'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

type EquipmentResourceDto = ILocationBase & {
  readonly accessibleLevels?: readonly (string | null)[] | null
  readonly effectivePosition?: IEquipmentPosition | null
  readonly linkedSensor?: {
    readonly id?: string | null
    readonly name: string
    readonly infuseId?: string | null
    readonly data?: unknown
  } | null
  readonly rollups?: unknown
}

interface EquipmentLinkedSensor {
  readonly id: string
  readonly name: string
  readonly infuseId: string | null
  readonly data: Readonly<Record<string, unknown>>
}

/** Normalized Equipment resource retained for rendering and lossless edits. */
export interface EquipmentResource {
  readonly id: string
  readonly name: string
  readonly code: string | null
  readonly stxResourceId: string | null
  readonly templateId: string | null
  readonly data: Readonly<Record<string, unknown>>
  readonly rollups: Readonly<Record<string, unknown>>
  readonly color: string
  readonly iconName: string
  readonly permittedCompanies: readonly string[]
  readonly isBookable: boolean
  readonly showInMap: boolean
  readonly workflowRestrictions: ILocationWorkflowRestrictions | null
  readonly maxOverlappingDeliveries: number | null
  readonly maxBookingDuration: number | null
  readonly assignedSitemaps: readonly (string | null)[]
  readonly assignedGlobes: readonly (string | null)[]
  readonly operatingIntervals: readonly IDateIntervalInput[]
  readonly parent: IDeliveryHierarchyParentInput | null
  readonly accessibleLevels: readonly (string | null)[]
  readonly linkedSensor: EquipmentLinkedSensor | null
  readonly effectivePosition: IEquipmentPosition | null
}

/** Equipment decoding failure produced when the transport value has no identity. */
export interface MissingEquipmentResourceId {
  readonly _tag: 'MissingEquipmentResourceId'
}

const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...value }
    : {}

const idsOf = (values: readonly unknown[] | null | undefined): string[] =>
  (values ?? [])
    .filter(value => value !== null && value !== undefined)
    .map(String)

const nullableIdsOf = (
  values: readonly (string | null)[] | null | undefined,
): Array<string | null> => [...(values ?? [])]

const linkedSensorOf = (
  sensor: EquipmentResourceDto['linkedSensor'],
): EquipmentLinkedSensor | null => {
  if (!sensor?.id) return null
  return {
    id: String(sensor.id),
    name: sensor.name,
    infuseId: sensor.infuseId ?? null,
    data: recordOf(sensor.data),
  }
}

/** Normalizes the generated GraphQL Equipment DTO at the transport boundary. */
export function parseEquipmentResource(
  location: EquipmentResourceDto,
): ResourceResult<EquipmentResource, MissingEquipmentResourceId> {
  if (location.id === null || location.id === undefined) {
    return resourceErr({ _tag: 'MissingEquipmentResourceId' })
  }

  return resourceOk({
    id: String(location.id),
    name: location.name,
    code: location.code ?? null,
    stxResourceId: location.stxResourceId ?? null,
    templateId: location.templateId ?? null,
    data: recordOf(location.data),
    rollups: recordOf(location.rollups),
    color: location.color,
    iconName: location.iconName ?? 'equipment',
    permittedCompanies: idsOf(location.permittedCompanies),
    isBookable: location.isBookable,
    showInMap: location.showInMap,
    workflowRestrictions: location.workflowRestrictions ?? null,
    maxOverlappingDeliveries: location.maxOverlappingDeliveries ?? null,
    maxBookingDuration: location.maxBookingDuration ?? null,
    assignedSitemaps: nullableIdsOf(location.assignedSitemaps),
    assignedGlobes: nullableIdsOf(location.assignedGlobes),
    operatingIntervals: (location.operatingIntervals ?? []).flatMap(interval =>
      interval
        ? [
            {
              startDate: interval.startDate,
              endDate: interval.endDate,
              isDaily: interval.isDaily,
              dayOfWeek: interval.dayOfWeek,
            },
          ]
        : [],
    ),
    parent: location.parent
      ? {
          parentId:
            location.parent.parentId === null ||
            location.parent.parentId === undefined
              ? null
              : String(location.parent.parentId),
        }
      : null,
    accessibleLevels: nullableIdsOf(location.accessibleLevels),
    linkedSensor: linkedSensorOf(location.linkedSensor),
    effectivePosition: location.effectivePosition
      ? {
          source: location.effectivePosition.source,
          latitude: location.effectivePosition.latitude ?? null,
          longitude: location.effectivePosition.longitude ?? null,
          lastUpdatedAt: location.effectivePosition.lastUpdatedAt ?? null,
        }
      : null,
  })
}

/** Applies one workflow-restriction WebSocket delta to Equipment resources. */
export function applyEquipmentWorkflowRestriction(
  resources: readonly EquipmentResource[],
  delta: {
    readonly id: string
    readonly item: IWorkflowLocationRestriction | null
  },
): readonly EquipmentResource[] {
  let changed = false
  const next = resources.map(resource => {
    const currentDurations =
      resource.workflowRestrictions?.maxBookingDurations?.flatMap(item =>
        item ? [item] : [],
      ) ?? []
    const currentOverlaps =
      resource.workflowRestrictions?.maxOverlappingBookings?.flatMap(item =>
        item ? [item] : [],
      ) ?? []
    const containsRestriction = [...currentDurations, ...currentOverlaps].some(
      restriction => String(restriction.id) === delta.id,
    )
    const receivesRestriction =
      delta.item !== null && String(delta.item.locationId) === resource.id
    if (!containsRestriction && !receivesRestriction) return resource

    const maxBookingDurations = currentDurations.filter(
      restriction => String(restriction.id) !== delta.id,
    )
    const maxOverlappingBookings = currentOverlaps.filter(
      restriction => String(restriction.id) !== delta.id,
    )
    if (receivesRestriction && delta.item) {
      const restriction = { ...delta.item, id: delta.item.id ?? delta.id }
      if (restriction.restrictionType === RestrictionType.MaxDuration) {
        maxBookingDurations.push(restriction)
      } else {
        maxOverlappingBookings.push(restriction)
      }
    }

    changed = true
    return {
      ...resource,
      workflowRestrictions: {
        maxBookingDurations,
        maxOverlappingBookings,
      },
    }
  })

  return changed ? next : resources
}

/** Projects an Equipment resource into the shared Resource UI record shape. */
export function toEquipmentResourceRecord(
  resource: EquipmentResource,
): IResourceRecord {
  const rootFields = {
    id: resource.id,
    name: resource.name,
    code: resource.code,
    stxResourceId: resource.stxResourceId,
    templateId: resource.templateId,
    color: resource.color,
    iconName: resource.iconName,
    parent: resource.parent,
  }

  return {
    ...rootFields,
    data: { ...resource.data, ...resource.rollups },
    rollups: { ...resource.rollups },
    root: {
      ...rootFields,
      permittedCompanies: [...resource.permittedCompanies],
      isBookable: resource.isBookable,
      showInMap: resource.showInMap,
      workflowRestrictions: resource.workflowRestrictions,
      operatingIntervals: resource.operatingIntervals.map(interval => ({
        ...interval,
      })),
      linkedSensor: resource.linkedSensor,
      effectivePosition: resource.effectivePosition,
    },
  }
}
